/**
 * Thin REST client. Mirrors the surface used by the Android app:
 *   auth, /me, /languages, /enroll/*, /enrollments/*, /api-keys, /sessions.
 *
 * All requests carry:
 *   - `X-API-Key`   (from local config; gate enforced by ApiKeyMiddleware)
 *   - `Authorization: Bearer <access_token>` when a JWT is loaded
 *
 * On a 401 from a JWT-protected endpoint we attempt a single refresh and
 * retry, mirroring the TokenAuthenticator in the Android client.
 */
import { getApiKey, resolveHttp } from "./config";
import { authStore } from "./store";

export type TokenPair = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
};

export type UserOut = {
  id: number;
  email: string;
  display_name: string;
  owner_lang: string;
  other_lang_override: string | null;
  fallback_other_lang: string;
  tts_voice_gender: "female" | "male";
  audio_retention_days: number;
  is_active: boolean;
  created_at: string;
  /** Server-computed from the admin email allowlist. Gates the admin nav
   *  only; the dashboard data itself still requires the X-Admin-Token. */
  is_admin?: boolean;
};

export type LanguageVoice = { short_name: string; gender: string; display_name: string };
export type LanguageInfo = {
  code: string;
  native_name: string;
  english_name: string;
  region: string | null;
  flag_emoji: string | null;
  group: string | null;
  stt_supported: boolean;
  tts_supported: boolean;
  /** True iff the server has an enrollment prompt pack for this language.
   *  Falsy languages still work for translation but the enrollment
   *  wizard would have to fall back to English prompt text. */
  enroll_prompts?: boolean;
  voices: LanguageVoice[];
};
export type LanguageCatalog = { languages: LanguageInfo[]; auto_detect_default: string[] };

export type EnrollmentSummary = {
  id: number;
  state: "draft" | "committed";
  active: boolean;
  voiced_sec: number | null;
  embedding_dim: number | null;
  created_at: string;
  clip_count: number;
};

export type EnrollStartResp = {
  enrollment_id: number;
  target_voiced_sec: number;
  hard_floor_sec: number;
  min_clip_voiced_sec: number;
  max_clipping_pct: number;
  min_snr_db: number;
};

export type EnrollClipResp = {
  accepted: boolean;
  reason: string | null;
  clip_voiced_sec: number;
  cumulative_voiced_sec: number;
  target_voiced_sec: number;
  enough: boolean;
  snr_db: number;
  clipping_pct: number;
  vad_backend: string;
};

export type EnrollPrompt = { id: string; text: string; audioUrl: string | null };
export type EnrollPromptsResp = { language: string; prompts: EnrollPrompt[] };

/** Wire shape returned by `/enroll/prompts` — parallel arrays. */
type EnrollPromptsWire = {
  language: string;
  prompts: string[];
  audio_urls: (string | null)[];
};

export type ApiKeyOut = {
  id: number;
  label: string;
  daily_audio_seconds_limit: number | null;
  daily_cost_usd_limit: number | null;
  max_session_duration_s: number | null;
  max_concurrent_sessions: number | null;
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
};
export type ApiKeyCreated = ApiKeyOut & { key: string };
export type ApiKeyUsage = {
  usage_date: string;
  audio_seconds: number;
  chars_in: number;
  chars_out: number;
  cost_usd: number;
  session_count: number;
};

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

async function rawFetch(path: string, init: RequestInit, withAuth: boolean): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  const apiKey = getApiKey();
  if (apiKey) headers.set("X-API-Key", apiKey);
  if (withAuth) {
    const tok = authStore.getState().tokens?.access_token;
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
  }
  return fetch(resolveHttp(path), { ...init, headers });
}

function formatDetail(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 validation errors: [{loc:[...], msg, type}, ...]
    return detail
      .map((d) => {
        if (d && typeof d === "object" && "msg" in d) {
          const loc = "loc" in d && Array.isArray((d as { loc: unknown[] }).loc)
            ? (d as { loc: unknown[] }).loc.filter((x) => x !== "body").join(".")
            : "";
          const msg = String((d as { msg: unknown }).msg);
          return loc ? `${loc}: ${msg}` : msg;
        }
        return JSON.stringify(d);
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    if ("msg" in detail) return String((detail as { msg: unknown }).msg);
    try { return JSON.stringify(detail); } catch { return "[unparseable error]"; }
  }
  return String(detail);
}

async function jsonOrThrow<T>(resp: Response): Promise<T> {
  if (resp.status === 204) return undefined as unknown as T;
  const ct = resp.headers.get("content-type") ?? "";
  const body = ct.includes("application/json") ? await resp.json().catch(() => null) : await resp.text();
  if (!resp.ok) {
    let msg = resp.statusText;
    if (typeof body === "object" && body && "detail" in body) {
      msg = formatDetail((body as { detail: unknown }).detail);
    } else if (typeof body === "string" && body) {
      msg = body;
    }
    throw new ApiError(resp.status, msg || `HTTP ${resp.status}`, body);
  }
  return body as T;
}

/** Authenticated request with one transparent refresh-and-retry on 401. */
async function authed<T>(path: string, init: RequestInit = {}): Promise<T> {
  let resp = await rawFetch(path, init, true);
  if (resp.status === 401 && authStore.getState().tokens?.refresh_token) {
    const ok = await tryRefresh();
    if (ok) resp = await rawFetch(path, init, true);
  }
  return jsonOrThrow<T>(resp);
}
async function open<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await rawFetch(path, init, false);
  return jsonOrThrow<T>(resp);
}

/** Like {@link authed} but returns the raw streaming Response (one 401
 *  refresh-and-retry). Callers read `resp.body` themselves — used for the
 *  NDJSON Learn stream where the body must not be buffered. */
async function authedRaw(path: string, init: RequestInit = {}): Promise<Response> {
  let resp = await rawFetch(path, init, true);
  if (resp.status === 401 && authStore.getState().tokens?.refresh_token) {
    const ok = await tryRefresh();
    if (ok) resp = await rawFetch(path, init, true);
  }
  return resp;
}

let refreshInFlight: Promise<boolean> | null = null;
export async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const cur = authStore.getState().tokens;
    if (!cur) return false;
    try {
      const next = await open<TokenPair>("/auth/refresh", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refresh_token: cur.refresh_token }),
      });
      authStore.getState().setTokens(next);
      return true;
    } catch (e) {
      // Only a genuine auth rejection — the refresh token itself is invalid,
      // expired, or revoked — should log the user out. A transient failure
      // (network blip → TypeError from fetch, or a 5xx from the server) must
      // NOT nuke the tokens and dump the user to sign-in mid-conversation;
      // it's retryable, so we keep the tokens and just report failure.
      if (isAuthRejection(e)) authStore.getState().logout();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * True only for failures that mean the refresh token is no longer usable and
 * the session is genuinely over. Network errors (not {@link ApiError}) and
 * server-side 5xx are treated as transient and return false, so the caller
 * keeps the tokens for a later retry.
 */
export function isAuthRejection(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false; // network / parse error → transient
  if (e.status === 401 || e.status === 403) return true;
  // Some servers return 400 invalid_grant for a dead/revoked refresh token.
  if (e.status === 400) return /invalid_grant|invalid.?token|expired|revoked|refresh/i.test(e.message);
  return false;
}

// --- Auth ----------------------------------------------------------------
export type SignupInput = {
  email: string;
  password: string;
  display_name: string;
  owner_lang: string;
  fallback_other_lang: string;
  tts_voice_gender: "female" | "male";
  device_id: string;
  device_label?: string;
};
export function signup(body: SignupInput): Promise<TokenPair> {
  return open("/auth/signup", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
}
export function login(body: { email: string; password: string; device_id: string; device_label?: string }): Promise<TokenPair> {
  return open("/auth/login", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
}
export function logout(refresh_token: string): Promise<void> {
  return authed("/auth/logout", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ refresh_token }) });
}
export function deleteAccount(): Promise<void> {
  return authed("/auth/account", { method: "DELETE" });
}

/**
 * Mint a short-lived, single-use ticket for a `/ws` upgrade. The access token
 * is sent in the Authorization header of this call (not the URL); the returned
 * ticket is what rides the WebSocket query string, so the long-lived JWT never
 * lands in proxy/CDN access logs.
 */
export type WsTicket = { ticket: string; expires_in: number };
export function wsTicket(): Promise<WsTicket> {
  return authed("/ws-ticket", { method: "POST" });
}

/**
 * Promote the current demo account to a real signed-up account. Server
 * keeps the underlying user row (so enrollment + devices carry over) and
 * returns a fresh token pair scoped to the same device.
 */
export type ClaimDemoInput = {
  email: string;
  password: string;
  display_name: string;
};
export function claimDemo(body: ClaimDemoInput): Promise<TokenPair> {
  return authed("/auth/claim-demo", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

// --- Demo (anon-account "Try demo" web flow) -----------------------------
export type DemoReplyRequest = {
  speaker_text: string;
  speaker_language: string;
  listener_language: string;
  voice_gender?: "female" | "male";
};
export type DemoReplyResponse = {
  reply_text: string;
  back_translation: string;
  audio_base64: string;
  audio_mime: string;
};
export function demoReply(body: DemoReplyRequest): Promise<DemoReplyResponse> {
  return authed("/demo/reply", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

// --- Learn a language ------------------------------------------------------
//
// Turn-based conversational practice (NOT the live WS pipeline — single
// speaker, no diarization). Contract for the server's `/learn/turn`:
// multipart form with a `payload` JSON field and an optional `audio` WAV
// (16 kHz mono PCM16) of the learner's spoken attempt. Server does
// ASR (target language) → tutor LLM → TTS and returns one exchange.
// The opener omits `audio`; "help me say it" sends `help_text` instead.

export type LearnLevel = "gentle" | "normal";

export type LearnTurnFeedback = {
  /** Whether the attempt was comprehensible in context. */
  understood: boolean;
  /** One short encouraging line, e.g. "Ravi understood you". */
  comment: string;
  /** A more natural phrasing in the target language (null if the attempt was fine). */
  better_phrase: string | null;
  better_translit: string | null;
};

export type LearnUserEcho = {
  /** What ASR heard, in target-language script. */
  text: string;
  translit: string;
  /** What it meant, in the learner's language. */
  meaning: string;
};

export type LearnSuggestion = {
  /** One possible learner response, in target-language script. */
  text: string;
  /** Informal Latin transliteration — the part the hint UI leads with. */
  translit: string;
  /** What it means, in the learner's language. */
  meaning: string;
};

/**
 * The `turn` stage of the Learn stream: everything textual for the exchange,
 * emitted the moment the tutor LLM finishes — before TTS audio. (The buffered
 * shape used to hold this hostage until synthesis completed.)
 */
export type LearnTurnData = {
  /** Echo of the learner's attempt; null on the opener / help turns. */
  user: LearnUserEcho | null;
  feedback: LearnTurnFeedback | null;
  /** The AI partner's next line. */
  reply_text: string;
  reply_translit: string;
  reply_en: string;
  /** A "you could say…" hint for the learner's next turn. */
  suggestion?: LearnSuggestion | null;
  /** Culture-appropriate persona name the LLM chose on the opener (e.g.
   *  "Ravi" for Telugu, "Marie" for French). Echo it back via
   *  `persona_name` on later turns to keep the character stable. */
  persona_name?: string | null;
};

export type LearnTurnRequest = {
  scenario_id: string;
  level: LearnLevel;
  /** Learner's native language (BCP-47, e.g. "en-US"). */
  native_language: string;
  /** Language being learned (e.g. "te-IN"). */
  target_language: string;
  /** Conversation so far, oldest first (target-language text). */
  history: { role: "partner" | "learner"; text: string }[];
  /** "Help me say it" request, in the learner's native language. */
  help_text?: string;
  /** Persona name from a previous turn's response — pins the character. */
  persona_name?: string;
};

/**
 * Stage callbacks for a Learn turn, invoked as each NDJSON stage arrives:
 *   onUser  — the ASR echo, the instant recognition finishes
 *   onTurn  — the tutor's textual turn (reply + feedback + hint), after the LLM
 *   onAudio — the TTS WAV (base64; "" when synthesis failed), last
 *   onError — a terminal failure: an HTTP status (404/501 = feature absent) or
 *             a mid-stream 503 from the server.
 */
export type LearnHandlers = {
  onUser?(user: LearnUserEcho | null): void;
  onTurn?(turn: LearnTurnData): void;
  onAudio?(audioBase64: string): void;
  onError?(code: number, message: string): void;
};

/** A running Learn turn. Start it (fires the request immediately, enabling the
 *  opener pre-warm), then attach handlers via {@link consume}. */
export type LearnTurnHandle = {
  /** Attach stage handlers, replaying any stages already buffered (e.g. a
   *  pre-warmed opener that finished before the view mounted). Resolves when
   *  the stream ends — it always resolves; failures surface via `onError`. */
  consume(handlers: LearnHandlers): Promise<void>;
  /** Cancel the in-flight request (e.g. the setup selection changed). */
  abort(): void;
};

export function learnTurn(req: LearnTurnRequest, audio?: Blob): LearnTurnHandle {
  const controller = new AbortController();
  const buffered: LearnStage[] = [];
  let sink: LearnHandlers | null = null;
  let resolveDone!: () => void;
  const done = new Promise<void>((res) => { resolveDone = res; });

  const emit = (s: LearnStage) => { if (sink) invokeStage(sink, s); else buffered.push(s); };

  void (async () => {
    const fd = new FormData();
    fd.append("payload", JSON.stringify(req));
    if (audio) fd.append("audio", audio, "turn.wav");
    let resp: Response;
    try {
      resp = await authedRaw("/learn/turn", { method: "POST", body: fd, signal: controller.signal });
    } catch (e) {
      if (!controller.signal.aborted) emit({ type: "error", code: 0, message: e instanceof Error ? e.message : String(e) });
      resolveDone();
      return;
    }
    if (!resp.ok) {
      // Pre-stream failure (auth / 400 same-language / 422 payload / 404|501
      // unavailable). Surface it uniformly as an error stage.
      let msg = `HTTP ${resp.status}`;
      try { const t = await resp.text(); if (t) msg = t; } catch { /* ignore */ }
      emit({ type: "error", code: resp.status, message: msg });
      resolveDone();
      return;
    }
    try {
      await readNdjson(resp, (o) => { const s = normalizeStage(o); if (s) emit(s); }, () => controller.signal.aborted);
    } catch (e) {
      if (!controller.signal.aborted) emit({ type: "error", code: 0, message: e instanceof Error ? e.message : String(e) });
    }
    resolveDone();
  })();

  return {
    consume(handlers) {
      sink = handlers;
      for (const s of buffered.splice(0)) invokeStage(handlers, s);
      return done;
    },
    abort() { controller.abort(); },
  };
}

/** Internal stage union parsed from the NDJSON body. */
type LearnStage =
  | { type: "user"; user: LearnUserEcho | null }
  | { type: "turn"; turn: LearnTurnData }
  | { type: "audio"; audioBase64: string }
  | { type: "error"; code: number; message: string };

function invokeStage(h: LearnHandlers, s: LearnStage): void {
  switch (s.type) {
    case "user": h.onUser?.(s.user); break;
    case "turn": h.onTurn?.(s.turn); break;
    case "audio": h.onAudio?.(s.audioBase64); break;
    case "error": h.onError?.(s.code, s.message); break;
  }
}

/** Map one raw NDJSON object to a typed stage; null for unknown stages. */
function normalizeStage(o: Record<string, unknown>): LearnStage | null {
  switch (String(o.type ?? "")) {
    case "user":
      return { type: "user", user: (o.user as LearnUserEcho | null) ?? null };
    case "turn":
      return {
        type: "turn",
        turn: {
          user: (o.user as LearnUserEcho | null) ?? null,
          feedback: (o.feedback as LearnTurnFeedback | null) ?? null,
          reply_text: String(o.reply_text ?? ""),
          reply_translit: String(o.reply_translit ?? ""),
          reply_en: String(o.reply_en ?? ""),
          suggestion: (o.suggestion as LearnSuggestion | null) ?? null,
          persona_name: (o.persona_name as string | null) ?? null,
        },
      };
    case "audio":
      return { type: "audio", audioBase64: String(o.audio_base64 ?? "") };
    case "error":
      return { type: "error", code: Number(o.code ?? 0), message: String(o.message ?? "unknown error") };
    default:
      return null;
  }
}

/** Read an NDJSON response body line by line, invoking `onObj` per JSON line. */
async function readNdjson(
  resp: Response,
  onObj: (o: Record<string, unknown>) => void,
  aborted: () => boolean,
): Promise<void> {
  const reader = resp.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buf = "";
  const drain = (chunk: string) => {
    buf += chunk;
    let nl: number;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (line) { try { onObj(JSON.parse(line)); } catch { /* skip a malformed line */ } }
    }
  };
  for (;;) {
    if (aborted()) { try { await reader.cancel(); } catch { /* ignore */ } return; }
    const { value, done } = await reader.read();
    if (done) break;
    drain(decoder.decode(value, { stream: true }));
  }
  const tail = buf.trim();
  if (tail) { try { onObj(JSON.parse(tail)); } catch { /* ignore */ } }
}

// --- /me, /languages -----------------------------------------------------
export function getMe(): Promise<UserOut> { return authed("/me"); }
export function patchMe(patch: Partial<Pick<UserOut, "display_name" | "owner_lang" | "other_lang_override" | "fallback_other_lang" | "tts_voice_gender" | "audio_retention_days">>): Promise<UserOut> {
  return authed("/me", { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(patch) });
}
export function getLanguages(): Promise<LanguageCatalog> { return open("/languages"); }

// --- Admin dashboard -----------------------------------------------------
//
// The admin surface needs a step-up secret (X-Admin-Token) on every request,
// on top of the usual X-API-Key + Bearer JWT. The token is held in memory and
// mirrored to sessionStorage only (never localStorage), so it is evicted when
// the tab closes and never persists to disk across sessions.

const ADMIN_TOKEN_KEY = "tw.adminToken";
let adminToken: string | null = (() => {
  try { return sessionStorage.getItem(ADMIN_TOKEN_KEY); } catch { return null; }
})();

export function setAdminToken(tok: string | null): void {
  adminToken = tok && tok.trim() ? tok.trim() : null;
  try {
    if (adminToken) sessionStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
    else sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch { /* ignore */ }
}
export function hasAdminToken(): boolean { return !!adminToken; }

async function adminRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  const apiKey = getApiKey();
  if (apiKey) headers.set("X-API-Key", apiKey);
  const tok = authStore.getState().tokens?.access_token;
  if (tok) headers.set("Authorization", `Bearer ${tok}`);
  if (adminToken) headers.set("X-Admin-Token", adminToken);
  return fetch(resolveHttp(path), { ...init, headers });
}

async function adminJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  let resp = await adminRaw(path, init);
  if (resp.status === 401 && authStore.getState().tokens?.refresh_token) {
    if (await tryRefresh()) resp = await adminRaw(path, init);
  }
  return jsonOrThrow<T>(resp);
}

export type AdminUsageDayPoint = {
  date: string;
  audio_seconds: number;
  cost_usd: number;
  session_count: number;
  chars_in: number;
  chars_out: number;
};
export type AdminLangCount = { lang: string; count: number };
export type AdminOverview = {
  total_users: number;
  total_sessions: number;
  total_utterances: number;
  total_turns: number;
  active_sessions: number;
  sessions_last_24h: number;
  usage_window_days: number;
  usage_by_day: AdminUsageDayPoint[];
  usage_totals: AdminUsageDayPoint;
  top_source_langs: AdminLangCount[];
  top_target_langs: AdminLangCount[];
};
export type AdminUsageRow = {
  usage_date: string;
  user_id: number;
  user_email: string;
  api_key_label: string;
  audio_seconds: number;
  chars_in: number;
  chars_out: number;
  cost_usd: number;
  session_count: number;
};
export type AdminSessionRow = {
  id: number;
  user_id: number;
  user_email: string;
  started_at: string;
  ended_at: string | null;
  utterance_count: number;
  turn_count: number;
};
export type AdminTurn = {
  id: number;
  turn_idx: number;
  label: string;
  src_lang: string | null;
  src_text: string | null;
  tgt_lang: string | null;
  tgt_text: string | null;
  has_turn_audio: boolean;
  has_tts_audio: boolean;
};
export type AdminUtterance = {
  id: number;
  utt_id: string;
  started_at: string | null;
  ended_at: string | null;
  has_full_audio: boolean;
  turns: AdminTurn[];
};
export type AdminSessionDetail = {
  id: number;
  user_id: number;
  user_email: string;
  started_at: string;
  ended_at: string | null;
  utterances: AdminUtterance[];
};

/** Probe admin access with the current token; resolves to the overview on
 *  success and throws ApiError (404 when unauthorized) otherwise. */
export function adminOverview(days = 14): Promise<AdminOverview> {
  return adminJson(`/admin/overview?days=${days}`);
}
export function adminUsage(days = 30, limit = 500): Promise<AdminUsageRow[]> {
  return adminJson(`/admin/usage?days=${days}&limit=${limit}`);
}
export function adminSessions(limit = 100, offset = 0): Promise<AdminSessionRow[]> {
  return adminJson(`/admin/sessions?limit=${limit}&offset=${offset}`);
}
export function adminSessionDetail(sid: number): Promise<AdminSessionDetail> {
  return adminJson(`/admin/sessions/${sid}`);
}

/** Fetch turn / utterance audio (with the admin step-up header) as a blob
 *  URL suitable for an <audio> element. Caller must URL.revokeObjectURL. */
export async function adminTurnAudioUrl(turnId: number, kind: "src" | "tts"): Promise<string> {
  const resp = await adminRaw(`/admin/turns/${turnId}/audio?kind=${kind}`);
  if (!resp.ok) throw new ApiError(resp.status, `audio fetch failed: ${resp.status}`);
  return URL.createObjectURL(await resp.blob());
}
export async function adminUtteranceAudioUrl(uid: number): Promise<string> {
  const resp = await adminRaw(`/admin/utterances/${uid}/audio`);
  if (!resp.ok) throw new ApiError(resp.status, `audio fetch failed: ${resp.status}`);
  return URL.createObjectURL(await resp.blob());
}

// --- Enroll --------------------------------------------------------------
export async function getEnrollPrompts(lang: string, n = 15): Promise<EnrollPromptsResp> {
  const wire = await authed<EnrollPromptsWire>(`/enroll/prompts?lang=${encodeURIComponent(lang)}&n=${n}`);
  // Derive a stable, server-accepted prompt id from the URL (16 hex sha) when
  // available, otherwise hash the text into the same [A-Za-z0-9_-]{1,64}
  // alphabet expected by /enroll/clip.
  const prompts: EnrollPrompt[] = wire.prompts.map((text, i) => {
    const url = wire.audio_urls[i] ?? null;
    let id = "";
    if (url) {
      const m = url.match(/\/([a-f0-9]{16})\.wav$/i);
      if (m) id = m[1]!;
    }
    if (!id) id = textToId(text);
    return { id, text, audioUrl: url };
  });
  return { language: wire.language, prompts };
}

/** Cheap 16-char alphanumeric digest of `text`. Browser-only (TextEncoder). */
function textToId(text: string): string {
  let h1 = 0xdeadbeef ^ 0;
  let h2 = 0x41c6ce57 ^ 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const lo = (h1 >>> 0).toString(16).padStart(8, "0");
  const hi = (h2 >>> 0).toString(16).padStart(8, "0");
  return lo + hi;
}

/** Fetch a prebaked enrollment prompt-audio WAV with auth and return a blob URL. */
export async function fetchEnrollAudioBlobUrl(path: string): Promise<string> {
  const resp = await rawFetch(path, {}, true);
  if (!resp.ok) throw new ApiError(resp.status, `Prompt audio fetch failed: ${resp.status}`);
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

export function enrollStart(): Promise<EnrollStartResp> {
  return authed("/enroll/start", { method: "POST" });
}
export async function enrollClip(enrollmentId: number, promptId: string, wav: Blob): Promise<EnrollClipResp> {
  const fd = new FormData();
  fd.append("enrollment_id", String(enrollmentId));
  fd.append("prompt_id", promptId);
  fd.append("file", wav, "clip.wav");
  return authed("/enroll/clip", { method: "POST", body: fd });
}
export async function enrollFinish(enrollmentId: number): Promise<{ enrollment_id: number; voiced_sec: number; embedding_dim: number; active: boolean }> {
  const fd = new FormData();
  fd.append("enrollment_id", String(enrollmentId));
  return authed("/enroll/finish", { method: "POST", body: fd });
}
export function listEnrollments(): Promise<EnrollmentSummary[]> { return authed("/enrollments"); }
export function activateEnrollment(id: number): Promise<EnrollmentSummary> {
  return authed(`/enrollments/${id}/activate`, { method: "POST" });
}
export function deleteEnrollment(id: number): Promise<void> {
  return authed(`/enrollments/${id}`, { method: "DELETE" });
}

// --- Sessions ------------------------------------------------------------
export type SessionListItem = {
  id: number;
  started_at: string;
  ended_at: string | null;
  utterance_count: number;
};
export type TurnOut = {
  id: number;
  turn_idx: number;
  label: string;
  t_start: number;
  t_end: number;
  src_lang: string | null;
  src_text: string | null;
  tgt_lang: string | null;
  tgt_text: string | null;
  has_turn_audio: boolean;
  has_tts_audio: boolean;
};
export type UtteranceOut = {
  id: number;
  utt_id: string;
  started_at: string | null;
  ended_at: string | null;
  turns: TurnOut[];
};
export type SessionDetailOut = {
  id: number;
  started_at: string;
  ended_at: string | null;
  utterances: UtteranceOut[];
};
export function listSessions(): Promise<SessionListItem[]> { return authed("/sessions"); }
export function getSession(id: number): Promise<SessionDetailOut> { return authed(`/sessions/${id}`); }
export function deleteSession(id: number): Promise<void> {
  return authed(`/sessions/${id}`, { method: "DELETE" });
}
export function turnAudioUrl(sid: number, uttId: string, idx: number, kind: "turn" | "tts"): string {
  return `${resolveHttp(`/sessions/${sid}/utt/${uttId}/turn/${idx}/${kind}.wav`)}`;
}
/** Fetches a turn's WAV with auth headers and returns a blob URL (caller must revoke). */
export async function fetchTurnAudioBlobUrl(sid: number, uttId: string, idx: number, kind: "turn" | "tts"): Promise<string> {
  const resp = await rawFetch(`/sessions/${sid}/utt/${uttId}/turn/${idx}/${kind}.wav`, {}, true);
  if (!resp.ok) throw new ApiError(resp.status, `Audio fetch failed: ${resp.status}`);
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

// --- API keys ------------------------------------------------------------
export function listApiKeys(): Promise<ApiKeyOut[]> { return authed("/api-keys"); }
export function createApiKey(label = "web"): Promise<ApiKeyCreated> {
  return authed("/api-keys", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ label }) });
}
export function getApiKeyUsage(id: number): Promise<ApiKeyUsage> { return authed(`/api-keys/${id}/usage`); }
export function revokeApiKey(id: number): Promise<void> { return authed(`/api-keys/${id}`, { method: "DELETE" }); }
